/**
 * Page Gesture Detection
 *
 * Single owner for page-wide user-activation listening. Capture phase, so a
 * page handler calling `stopPropagation()` cannot hide the gesture.
 */

/** Events that carry a user activation. */
const GESTURE_EVENTS = ['click', 'touchstart', 'touchend', 'keydown'] as const;

/**
 * Whether a user gesture is being handled right now.
 *
 * False on browsers without `navigator.userActivation`.
 *
 * @returns True during activation-triggering input handling
 */
export function hasTransientActivation(): boolean {
  return typeof navigator !== 'undefined' && navigator.userActivation?.isActive === true;
}

/**
 * Listen for the next qualifying page gesture.
 *
 * Re-armable: call again for a further gesture. The callback fires at most once
 * per arming, and every listener is removed together.
 *
 * @param callback - Invoked inside the gesture, so it carries the activation
 * @returns Disposer; safe to call after the callback has fired
 */
export function armGestureOnce(callback: () => void): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  let armed = true;

  function handler(): void {
    if (!armed) return;
    dispose();
    callback();
  }

  function dispose(): void {
    if (!armed) return;
    armed = false;
    for (const type of GESTURE_EVENTS) {
      document.removeEventListener(type, handler, { capture: true });
    }
  }

  for (const type of GESTURE_EVENTS) {
    document.addEventListener(type, handler, { capture: true, passive: true });
  }

  return dispose;
}
